<template>
	<div ref="divGoogleLoginContainer" :style="{ width: containerWidth }" v-cloak>
		<div class="anti-flicker" :aria-busy="sdkLoading">
			<div ref="btnGoogleLogin" class="google-button-host" :class="{ 'button-ready': buttonReady }" :inert="!buttonReady" :aria-hidden="!buttonReady" />
			<div v-if="!buttonReady" class="login-placeholder">
				<span v-if="sdkLoading || sdkReady" role="status">Loading sign-in...</span>
				<button v-else type="button" @click="loadGoogleLoginButton">Retry Google sign-in</button>
			</div>
		</div>
		<div class="mt-1 mb-3 text-center" ref="divGoogleLogin" v-show="error">
			<span class="error-msg">Could not sign in with Google. Please <a href="/contact" rel="nofollow" target="_blank">contact support</a> if this keeps happening.</span>
		</div>
	</div>
</template>




<script>
	import { mixinAxios } from '@/js/shared/VueMixins.js';
	import { getClientDateTimeString, getUrlParameterByName, parseWidthValue } from '@/js/shared/utils/common';
	import { ensureGoogleSdk } from '@/js/shared/utils/sdkLoader';
	// Docs/Reference:
	// https://developers.google.com/identity/gsi/web/guides/overview
	// https://developers.google.com/identity/gsi/web/reference/js-reference
	// https://developers.google.com/identity/gsi/web/tools/configurator

	// This components depends on:
	// https://accounts.google.com/gsi/client

	export default {
		mixins: [mixinAxios],
		data: () => ({
			error : false,
			sdkLoading: true,
			sdkReady: false,
			buttonReady: false,
			buttonRenderAttempt: 0,
			renderedGoogleButtonWidth: 0,
			resizeObserver: null
		}),
		props: {
			clientId    : { type: String, required: true },
			styling     : { type: String, default: 'styling0' },
			urlRedirect : { type: String, default: null },
			width       : { type: [String, Number], default: 300 },
			autoPrompt  : { type: Boolean, default: true }
		},
		mounted() {
			this.setWidth();
			this.setupResizeObserver();
			this.loadGoogleLoginButton();
		},
		beforeUnmount() {
			this.googleUnmounted = true;
			this.buttonRenderAttempt++;
			this.teardownResizeObserver();
		},
		methods: {
			async loadGoogleLoginButton() {
				this.sdkLoading = true;
				this.buttonReady = false;
				this.error = false;
				try {
					await ensureGoogleSdk();
					if (this.googleUnmounted) return;
					this.initGoogleLoginButton();
					this.sdkReady = true;
					await this.$nextTick();
					if (this.googleUnmounted) return;
					await this.renderGoogleLoginButton(true);
					if (this.googleUnmounted) return;
					this.autoPromptGoogleLogin();
				} catch {
					this.sdkReady = false;
				} finally {
					this.sdkLoading = false;
				}
			},
			setWidth() {
				var width = parseWidthValue(this.width);
				this.divGoogleLoginContainer.style.width = width;
				this.divGoogleLogin.style.width = width;
			},
			getRenderedButtonWidth() {
				var width = parseWidthValue(this.width);
				var widthIsPercentage = (typeof width === 'string') && width.endsWith('%');
				var number = parseInt(width);

				if (widthIsPercentage) {
					return Math.max(0, Math.round(this.divGoogleLoginContainer.clientWidth));
				}

				return Number.isNaN(number)
					? 0
					: number;
			},
			redirect() {
				var redirectionNotNull = (this.redirectionUrl != null);

				if (redirectionNotNull) {
					window.location.href = this.redirectionUrl;
				} else {
					window.location.reload();
				}
			},
			handleCredentialResponse(credentialResponse) {
				this.error = false;
				var googleAccessToken = {
					idToken: credentialResponse.credential
				};

				var signInInfo = {
					googleAccessToken: googleAccessToken,
					clientDateTime: getClientDateTimeString()
				};

				return this.mixinAxios().post('/api/guest/GoogleLoginAttempt', signInInfo)
					.then((resGoogleLoginAttempt) => {
						if (resGoogleLoginAttempt.data.success) {
							this.redirect();
						} else {
							this.error = true;
						}
					})
					.catch(() => {
						this.error = true;
					});
			},
			initGoogleLoginButton() {
				window.google.accounts.id.initialize({
					client_id: this.clientId,
					callback: this.handleCredentialResponse,
					cancel_on_tap_outside: true,
					ux_mode: 'popup',
					auto_select: false,
					itp_support: true,
					use_fedcm_for_prompt: true
				});
			},
			async renderGoogleLoginButton(force = false) {
				if (!this.sdkReady || !window?.google?.accounts?.id) {
					return;
				}

				var width = this.getRenderedButtonWidth();
				if (width <= 0) {
					return;
				}

				if (!force && (width == this.renderedGoogleButtonWidth) && this.btnGoogleLogin.childElementCount > 0) {
					return;
				}

				const attempt = ++this.buttonRenderAttempt;
				this.buttonReady = false;
				this.btnGoogleLogin.innerHTML = '';

				window.google.accounts.id.renderButton(
					this.btnGoogleLogin, {
						type: 'standard',
						theme: 'filled_blue',
						size: 'large',
						text: 'signin_with',
						shape: 'rectangular',
						logo_alignment: 'left',
						click_listener: () => { this.error = false; },
						width: width
					}
				);

				this.renderedGoogleButtonWidth = width;
				// Allow Google's CSS to request its font before waiting for font readiness.
				await new Promise(resolve => requestAnimationFrame(resolve));
				if (this.googleUnmounted || attempt != this.buttonRenderAttempt) return;
				let timeout;
				try {
					await Promise.race([
						document.fonts?.ready ?? Promise.resolve(),
						new Promise((resolve, reject) => { timeout = setTimeout(() => reject(new Error('Google button font loading timed out.')), 15000); })
					]);
					await new Promise(resolve => requestAnimationFrame(resolve));
					if (!this.googleUnmounted && attempt == this.buttonRenderAttempt) this.buttonReady = true;
				} catch {
					if (!this.googleUnmounted && attempt == this.buttonRenderAttempt) {
						this.sdkReady = false;
						this.sdkLoading = false;
					}
				} finally {
					clearTimeout(timeout);
				}
			},
			setupResizeObserver() {
				this.teardownResizeObserver();

				if (typeof ResizeObserver === 'undefined') {
					return;
				}

				this.resizeObserver = new ResizeObserver(() => {
					this.renderGoogleLoginButton();
				});
				this.resizeObserver.observe(this.divGoogleLoginContainer);
			},
			teardownResizeObserver() {
				if (this.resizeObserver) {
					this.resizeObserver.disconnect();
					this.resizeObserver = null;
				}
			},
			autoPromptGoogleLogin() {
				if (this.autoPrompt) {
					google.accounts.id.prompt();
				}
			}
		},
		computed: {
			containerWidth() {
				return parseWidthValue(this.width);
			},
			btnGoogleLogin() {
				return this.$refs['btnGoogleLogin'];
			},
			divGoogleLogin() {
				return this.$refs['divGoogleLogin'];
			},
			divGoogleLoginContainer() {
				return this.$refs['divGoogleLoginContainer'];
			},
			redirectionUrl() {
				var urlFromParam = getUrlParameterByName('redirect');

				return (urlFromParam != null)
					? urlFromParam
					: (this.urlRedirect != null)
					? this.urlRedirect
					: null;
			}
		},
		watch: {
			width(newVal, oldVal) {
				this.setWidth();
				this.renderedGoogleButtonWidth = 0;
				this.$nextTick(() => {
					this.renderGoogleLoginButton(true);
				});
			}
		}
	};
</script>




<style scoped>
	[v-cloak] {
		display:none;
	}


	/* common */

	.anti-flicker {
		position: relative;
		overflow: hidden;
		height: 42px;
		min-height: 42px;
		border-top: 1px solid transparent;
		border-bottom: 1px solid transparent;
	}

	.google-button-host {
		position: absolute;
		inset: 0;
		height: 40px;
		overflow: hidden;
		opacity: 0;
		pointer-events: none;
	}

	.google-button-host.button-ready {
		opacity: 1;
		pointer-events: auto;
	}

	.google-button-host:focus-within {
		outline: 2px solid white;
		outline-offset: -3px;
	}

	.login-placeholder {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px solid #dadce0;
		border-radius: 4px;
		background: #f8f9fa;
		font-size: 14px;
		color: #5f6368;
	}

	.login-placeholder button {
		width: 100%;
		height: 100%;
		border: 0;
		background: transparent;
		color: inherit;
	}

	.error-msg {
		color: red;
	}

	/* common */


	/* styling 0 */

	.styling0 {
		font-family: 'Roboto', sans-serif;
		font-weight: normal;
		font-size: 16px;
		color: #4C4C4C;
		height: 44px;
		background: #FFFFFF;
		padding:0px 12px;
		display:flex;
		align-items:center;
		justify-content:center;
		border:thin solid #C0C0C0;
		border-radius: 4px;
		cursor:pointer;
	}

	.styling0 img {
		height:22px;
		width:22px;
	}

	.styling0 span {
		margin:3px 0px 0px 10px;
	}

	/* styling 0 */

</style>
